/**
 * AchievementUnlock - Achievement unlock notification
 *
 * Features:
 * - Slides in from the right side
 * - Golden glow effect
 * - Achievement icon with sparkle animation
 * - Sound effect on appearance
 * - Links to achievements page
 * - Auto-dismiss with progress bar
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SparkleEffect } from './ConfettiEffect';
import audioManager from '../../services/audioManager';

// Achievement rarity colors
const RARITY_STYLES = {
  common: {
    borderColor: '#95a5a6',
    glowColor: 'rgba(149, 165, 166, 0.4)',
    textColor: '#bdc3c7',
    bgGradient: 'from-gray-700 to-gray-800',
  },
  uncommon: {
    borderColor: '#27ae60',
    glowColor: 'rgba(39, 174, 96, 0.4)',
    textColor: '#2ecc71',
    bgGradient: 'from-emerald-900 to-emerald-950',
  },
  rare: {
    borderColor: '#3498db',
    glowColor: 'rgba(52, 152, 219, 0.5)',
    textColor: '#3498db',
    bgGradient: 'from-blue-900 to-blue-950',
  },
  epic: {
    borderColor: '#9b59b6',
    glowColor: 'rgba(155, 89, 182, 0.5)',
    textColor: '#9b59b6',
    bgGradient: 'from-purple-900 to-purple-950',
  },
  legendary: {
    borderColor: '#f1c40f',
    glowColor: 'rgba(241, 196, 15, 0.6)',
    textColor: '#f1c40f',
    bgGradient: 'from-yellow-900 to-amber-950',
  },
};

const AchievementUnlock = ({
  isVisible,
  achievement,
  onComplete,
  autoDismissDelay = 5000,
  position = 'top-right', // 'top-right', 'top-left', 'bottom-right', 'bottom-left'
}) => {
  const navigate = useNavigate();
  const [showSparkles, setShowSparkles] = useState(false);
  const [progress, setProgress] = useState(100);

  const rarity = achievement?.rarity || 'common';
  const styles = RARITY_STYLES[rarity] || RARITY_STYLES.common;

  // Position classes
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
  };

  // Slide direction based on position
  const slideDirection = position.includes('right') ? 100 : -100;

  useEffect(() => {
    if (isVisible) {
      // Play achievement sound
      audioManager.playAchievement();

      // Show sparkles after a delay
      setTimeout(() => setShowSparkles(true), 300);

      // Progress bar countdown
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / autoDismissDelay) * 100);
        setProgress(remaining);

        if (remaining <= 0) {
          clearInterval(interval);
          onComplete?.();
        }
      }, 50);

      return () => {
        clearInterval(interval);
        setShowSparkles(false);
        setProgress(100);
      };
    }
  }, [isVisible, autoDismissDelay, onComplete]);

  const handleClick = () => {
    navigate('/achievements');
    onComplete?.();
  };

  if (!achievement) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={`fixed ${positionClasses[position]} z-[9999] cursor-pointer`}
          initial={{ x: slideDirection, opacity: 0, scale: 0.8 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          exit={{ x: slideDirection, opacity: 0, scale: 0.8 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25,
          }}
          onClick={handleClick}
        >
          {/* Main container */}
          <motion.div
            className={`
              relative w-80 bg-gradient-to-br ${styles.bgGradient}
              border-2 rounded-lg overflow-hidden
              shadow-[0_10px_40px_rgba(0,0,0,0.5)]
            `}
            style={{
              borderColor: styles.borderColor,
              boxShadow: `0 10px 40px rgba(0,0,0,0.5), 0 0 30px ${styles.glowColor}`,
            }}
            animate={{
              boxShadow: [
                `0 10px 40px rgba(0,0,0,0.5), 0 0 20px ${styles.glowColor}`,
                `0 10px 40px rgba(0,0,0,0.5), 0 0 40px ${styles.glowColor}`,
                `0 10px 40px rgba(0,0,0,0.5), 0 0 20px ${styles.glowColor}`,
              ],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-2 bg-black/30 border-b border-white/10">
              <motion.span
                className="text-lg"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.5, repeat: 3 }}
              >
                🏆
              </motion.span>
              <span
                className="text-sm font-bold uppercase tracking-wider"
                style={{ color: styles.textColor }}
              >
                Achievement Unlocked!
              </span>
            </div>

            {/* Content */}
            <div className="p-4 flex items-center gap-4">
              {/* Achievement icon with sparkles */}
              <div className="relative flex-shrink-0">
                <motion.div
                  className="w-16 h-16 rounded-lg flex items-center justify-center text-3xl"
                  style={{
                    backgroundColor: `${styles.borderColor}20`,
                    border: `2px solid ${styles.borderColor}`,
                    boxShadow: `inset 0 0 20px ${styles.glowColor}`,
                  }}
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                >
                  {achievement.icon || '⭐'}
                </motion.div>

                {/* Sparkle effect */}
                <SparkleEffect
                  isActive={showSparkles}
                  particleCount={12}
                  colors={[styles.borderColor, '#ffffff', styles.textColor]}
                  originX="50%"
                  originY="50%"
                  size={60}
                />
              </div>

              {/* Achievement details */}
              <div className="flex-1 min-w-0">
                <motion.h4
                  className="text-white font-bold text-base truncate"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  {achievement.name}
                </motion.h4>
                <motion.p
                  className="text-text-muted text-sm mt-1 line-clamp-2"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  {achievement.description}
                </motion.p>
                {achievement.reward && (
                  <motion.div
                    className="mt-2 text-xs text-accent-yellow font-semibold"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    +{achievement.reward} XP
                  </motion.div>
                )}
              </div>
            </div>

            {/* Progress bar for auto-dismiss */}
            <div className="h-1 bg-black/30">
              <motion.div
                className="h-full"
                style={{ backgroundColor: styles.borderColor }}
                initial={{ width: '100%' }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.05, ease: 'linear' }}
              />
            </div>

            {/* Click hint */}
            <motion.div
              className="absolute bottom-2 right-3 text-[10px] text-text-muted"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ delay: 1 }}
            >
              Click to view
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AchievementUnlock;
